import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Bell,
  Bookmark,
  Building2,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Eye,
  Globe2,
  LockKeyhole,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
  Smartphone,
  UserRound,
  UsersRound,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError, api } from "@/lib/api";
import { SEO } from "@/lib/seo";
import {
  REFERRAL_METHOD_OPTIONS,
  WAIT_BUCKET_OPTIONS,
  referralMethodLabel,
  waitBucketLabel,
  type ReferralMethod,
  type WaitBucket,
} from "@/lib/availability";
import "@/styles/onboarding-mockup.css";

type IdentityChoice = "yes" | "no" | "not_sure";
type AvailabilityChoice = "yes" | "urgent" | "no" | "not_sure";
type ApprovalState = "pending" | "approved" | "changes" | "later";
type PersistedAvailabilityChoice = "yes" | "urgent_only" | "no";

interface TrustItem {
  icon: LucideIcon;
  title: string;
  copy: string;
}

interface WizardStep {
  icon: LucideIcon;
  shortTitle: string;
  eyebrow: string;
  title: string;
  copy: string;
}

interface OptionButtonProps {
  icon: LucideIcon;
  label: string;
  helper?: string;
  selected?: boolean;
  tone?: "default" | "positive" | "urgent" | "danger" | "muted";
  onClick: () => void;
}

interface SpecialistMe {
  profile: {
    id: string;
    accepting_referrals: PersistedAvailabilityChoice;
    wait_time_days: number | null;
    wait_bucket?: WaitBucket | null;
    referral_method?: ReferralMethod | null;
    referral_email?: string | null;
    referral_fax?: string | null;
    referral_notes?: string | null;
    specialties: string[];
    special_interests: string[] | null;
    telehealth: boolean;
    accepts_bulk_bill: boolean;
    verified: boolean;
    claimed: boolean;
    onboarding_status: string;
    facility_name: string | null;
    facility_slug: string | null;
  } | null;
  claim: {
    id: string;
    status: string;
    created_at: string;
    facility_name: string | null;
    facility_id: string | null;
  } | null;
}

interface DisplayProfile {
  initials: string;
  name: string;
  specialty: string;
  practice: string;
  location: string;
  phone: string;
  website: string;
  about: string;
  isDemo: boolean;
}

const trustItems: TrustItem[] = [
  {
    icon: ShieldCheck,
    title: "Trusted & source-checked",
    copy: "Nothing is published until you approve it.",
  },
  {
    icon: Clock3,
    title: "Usually 3 minutes",
    copy: "Short, click-first checks with no long forms.",
  },
  {
    icon: CircleHelp,
    title: "Not sure is okay",
    copy: "Uncertain answers can be checked later.",
  },
  {
    icon: LockKeyhole,
    title: "No patient data",
    copy: "This link only collects profile information.",
  },
];

const wizardSteps: WizardStep[] = [
  {
    icon: LockKeyhole,
    shortTitle: "Welcome",
    eyebrow: "Private specialist link",
    title: "Welcome, Dr Jane Smith.",
    copy: "HeartCare Specialist Centre has prepared your PulseBoard profile. Confirm only the information GPs need to refer accurately.",
  },
  {
    icon: ShieldCheck,
    shortTitle: "Identity",
    eyebrow: "Step 1 of 4",
    title: "Is this Dr Jane Smith?",
    copy: "Please confirm the core details before anything else is shown to GPs.",
  },
  {
    icon: UsersRound,
    shortTitle: "Referrals",
    eyebrow: "Step 2 of 4",
    title: "Are you accepting referrals?",
    copy: "Choose the closest answer. If unsure, choose Not sure and PulseBoard will check again later.",
  },
  {
    icon: Bell,
    shortTitle: "Updates",
    eyebrow: "Step 3 of 4",
    title: "Who keeps this profile updated?",
    copy: "PulseBoard can remind the right person so your referral information stays current.",
  },
  {
    icon: Eye,
    shortTitle: "Preview",
    eyebrow: "Step 4 of 4",
    title: "Review what GPs will see.",
    copy: "This is the public referral-facing profile. No patient information appears here.",
  },
  {
    icon: CheckCircle2,
    shortTitle: "Approved",
    eyebrow: "Approval complete",
    title: "Your PulseBoard profile is ready.",
    copy: "The approved profile can now be kept current with lightweight reminders.",
  },
];

const ownerOptions = [
  { icon: Building2, label: "Reception / practice manager", helper: "Best for admin-led updates" },
  { icon: UserRound, label: "Specialist", helper: "You will receive reminders" },
  { icon: UsersRound, label: "Both", helper: "Either person can confirm" },
  { icon: ShieldCheck, label: "PulseBoard admin", helper: "PulseBoard will follow up" },
];

function OptionButton({
  icon: Icon,
  label,
  helper,
  selected,
  tone = "default",
  onClick,
}: OptionButtonProps) {
  return (
    <button
      type="button"
      className={`onb-option is-${tone}${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span className="onb-option-icon">
        <Icon size={26} strokeWidth={1.9} />
      </span>
      <span>
        <strong>{label}</strong>
        {helper && <small>{helper}</small>}
      </span>
      {selected && (
        <em aria-hidden="true">
          <Check size={15} />
        </em>
      )}
    </button>
  );
}

function PillButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`onb-pill${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function ProgressBars({ activeIndex }: { activeIndex: number }) {
  return (
    <div className="onb-progress-bars" aria-hidden="true">
      {wizardSteps.slice(1, 5).map((step, index) => (
        <span
          key={step.shortTitle}
          className={index < activeIndex ? "is-active" : undefined}
        />
      ))}
    </div>
  );
}

function useSpecialistMe(enabled: boolean) {
  return useQuery<SpecialistMe>({
    queryKey: ["specialist-me"],
    queryFn: () => api.get<SpecialistMe>("/v1/specialist/me"),
    enabled,
    retry: false,
  });
}

function acceptingToAvailability(value?: PersistedAvailabilityChoice): AvailabilityChoice {
  if (value === "urgent_only") return "urgent";
  if (value === "no") return "no";
  return "yes";
}

function availabilityToAccepting(
  value: AvailabilityChoice,
  fallback: PersistedAvailabilityChoice,
): PersistedAvailabilityChoice {
  if (value === "urgent") return "urgent_only";
  if (value === "no") return "no";
  if (value === "not_sure") return fallback;
  return "yes";
}

function waitBucketFromDays(days: number | null | undefined): WaitBucket | null {
  if (days === undefined || days === null) return null;
  if (days <= 14) return "under_2w";
  if (days <= 42) return "w2_6";
  if (days <= 84) return "w6_12";
  return "over_3m";
}

function initialsFromName(name: string): string {
  const cleaned = name.replace(/^Dr\s+/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
  return initials || "PB";
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}

export default function SpecialistOnboarding() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const queryClient = useQueryClient();
  const specialistQuery = useSpecialistMe(isAuthenticated && !authLoading);
  const specialist = specialistQuery.data?.profile ?? null;
  const claim = specialistQuery.data?.claim ?? null;
  const canPersist = Boolean(isAuthenticated && specialist);
  const [activeStep, setActiveStep] = useState(0);
  const [identityChoice, setIdentityChoice] = useState<IdentityChoice>("yes");
  const [availability, setAvailability] = useState<AvailabilityChoice>("yes");
  const [waitBucket, setWaitBucket] = useState<WaitBucket | null>("w2_6");
  const [referralMethod, setReferralMethod] = useState<ReferralMethod | null>(
    "medical_objects",
  );
  const [referralEmail, setReferralEmail] = useState("");
  const [referralFax, setReferralFax] = useState("");
  const [referralNotes, setReferralNotes] = useState("");
  const [owner, setOwner] = useState("Reception / practice manager");
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [approvalState, setApprovalState] = useState<ApprovalState>("pending");
  const [hasHydrated, setHasHydrated] = useState(false);
  const [localSaveNotice, setLocalSaveNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!specialist || hasHydrated) return;
    setAvailability(acceptingToAvailability(specialist.accepting_referrals));
    setWaitBucket(
      specialist.wait_bucket ?? waitBucketFromDays(specialist.wait_time_days),
    );
    setReferralMethod(specialist.referral_method ?? null);
    setReferralEmail(specialist.referral_email ?? "");
    setReferralFax(specialist.referral_fax ?? "");
    setReferralNotes(specialist.referral_notes ?? "");
    setHasHydrated(true);
  }, [hasHydrated, specialist]);

  const displayProfile = useMemo<DisplayProfile>(() => {
    const isDemo = !specialist;
    const name = user?.name?.trim() || (isDemo ? "Dr Jane Smith" : "Your specialist profile");
    const specialty = specialist?.specialties?.[0] || (isDemo ? "Cardiology" : "Specialist");
    const practice = specialist?.facility_name || claim?.facility_name || (isDemo ? "HeartCare Specialist Centre" : "Your practice");

    return {
      initials: initialsFromName(name),
      name,
      specialty,
      practice,
      location: isDemo ? "Melbourne VIC 3000" : "Confirmed in your PulseBoard profile",
      phone: isDemo ? "(03) 9001 2345" : "Managed by your practice",
      website: isDemo ? "www.heartcare.com.au" : "Shown from the public directory when available",
      about: isDemo
        ? "Consultant Cardiologist with expertise in general cardiology, preventive heart health and cardiac imaging."
        : `${specialty} profile for ${practice}.`,
      isDemo,
    };
  }, [claim?.facility_name, specialist, user?.name]);

  const profileRows = useMemo(
    () => [
      { icon: UserRound, label: "Name", value: displayProfile.name },
      { icon: Activity, label: "Specialty", value: displayProfile.specialty },
      { icon: Building2, label: "Practice", value: displayProfile.practice },
      { icon: MapPin, label: "Location", value: displayProfile.location },
      { icon: Phone, label: "Phone", value: displayProfile.phone },
      { icon: Globe2, label: "Website", value: displayProfile.website, link: displayProfile.isDemo },
    ],
    [displayProfile],
  );

  const saveAvailability = useMutation({
    mutationFn: () => {
      if (!specialist) {
        return Promise.resolve(null);
      }

      return api.patch("/v1/specialist/availability", {
        acceptingReferrals: availabilityToAccepting(
          availability,
          specialist.accepting_referrals,
        ),
        waitBucket,
        referralMethod,
        ...(referralMethod === "email" && referralEmail.trim()
          ? { referralEmail: referralEmail.trim() }
          : {}),
        ...(referralMethod === "fax" && referralFax.trim()
          ? { referralFax: referralFax.trim() }
          : {}),
        ...(referralNotes.trim() ? { referralNotes: referralNotes.trim() } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["specialist-me"] });
    },
  });

  const active = wizardSteps[activeStep] ?? wizardSteps[0]!;
  const progressPercent = Math.round(((activeStep + 1) / wizardSteps.length) * 100);

  const stageHeading = useMemo(() => {
    if (activeStep === 0) {
      return {
        ...active,
        title: `Welcome, ${displayProfile.name}.`,
        copy: `${displayProfile.practice} has prepared your PulseBoard profile. Confirm only the information GPs need to refer accurately.`,
      };
    }

    if (activeStep === 1) {
      return {
        ...active,
        title: `Is this ${displayProfile.name}?`,
      };
    }

    return active;
  }, [active, activeStep, displayProfile.name, displayProfile.practice]);

  const referralSummary = useMemo(() => {
    const labels: Record<AvailabilityChoice, string> = {
      yes: "Accepting referrals",
      urgent: "Urgent referrals only",
      no: "Not accepting referrals",
      not_sure: "Referral status to be checked",
    };

    return labels[availability];
  }, [availability]);

  const primaryLabel = useMemo(() => {
    if (activeStep === 0) return "Start confirmation";
    if (activeStep === wizardSteps.length - 2) {
      return saveAvailability.isPending ? "Saving..." : "Approve profile";
    }
    if (activeStep === wizardSteps.length - 1) return "Approved";
    return "Continue";
  }, [activeStep, saveAvailability.isPending]);

  const goNext = () => {
    setActiveStep((step) => Math.min(step + 1, wizardSteps.length - 1));
  };

  const goBack = () => {
    setActiveStep((step) => Math.max(step - 1, 0));
  };

  const handleApprove = async () => {
    setLocalSaveNotice(null);
    setApprovalState("pending");

    if (!canPersist) {
      setLocalSaveNotice(
        "Demo mode: this approval preview did not write to the live specialist portal.",
      );
      setApprovalState("approved");
      setActiveStep(5);
      return;
    }

    try {
      await saveAvailability.mutateAsync();
      setApprovalState("approved");
      setActiveStep(5);
    } catch (error) {
      setLocalSaveNotice(errorMessage(error));
    }
  };

  const handlePrimaryAction = () => {
    if (activeStep === wizardSteps.length - 2) {
      void handleApprove();
      return;
    }

    goNext();
  };

  const renderStep = () => {
    if (isAuthenticated && specialistQuery.isLoading) {
      return (
        <div className="onb-system-state">
          <span><Clock3 size={28} /></span>
          <h2>Loading your specialist profile.</h2>
          <p>PulseBoard is checking the prepared profile attached to this account.</p>
        </div>
      );
    }

    if (isAuthenticated && specialistQuery.isError) {
      return (
        <div className="onb-system-state">
          <span><CircleHelp size={28} /></span>
          <h2>We could not load your profile.</h2>
          <p>{errorMessage(specialistQuery.error)}</p>
          <button
            type="button"
            className="onb-action-button is-primary"
            onClick={() => void specialistQuery.refetch()}
          >
            Try again
          </button>
        </div>
      );
    }

    if (isAuthenticated && specialistQuery.data && !specialist) {
      return (
        <div className="onb-system-state">
          <span><ShieldCheck size={28} /></span>
          <h2>Your specialist profile is not prepared yet.</h2>
          <p>
            Claim or create your specialist profile first. Once it exists,
            this onboarding flow will use the live profile details here.
          </p>
          <Link className="onb-action-button is-primary" to="/specialist/availability">
            Claim profile
          </Link>
        </div>
      );
    }

    if (activeStep === 0) {
      return (
        <div className="onb-step-grid onb-welcome-grid">
          <section className="onb-welcome-panel">
            <span className="onb-secure-chip">
              <LockKeyhole size={16} /> Secure specialist link
            </span>
            <div className="onb-avatar">{displayProfile.initials}</div>
            <h2>Confirm your referral profile in a few clicks.</h2>
            <p>
              This link is only for your PulseBoard profile. You can say no or not sure at
              any step, and your practice can help with changes after approval.
            </p>
            <div className="onb-assurance-list">
              <span><Clock3 size={16} /> Usually 3 minutes</span>
              <span><CircleHelp size={16} /> Not sure is okay</span>
              <span><LockKeyhole size={16} /> No patient information</span>
            </div>
          </section>

          <section className="onb-detail-panel">
            <h3>Your prepared profile</h3>
            <div className="onb-detail-list">
              {profileRows.slice(0, 4).map((row) => (
                <div key={row.label}>
                  <span><row.icon size={18} /> {row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="onb-not-me-button"
              onClick={() => setIdentityChoice("no")}
            >
              <UserRound size={19} /> This is not me
            </button>
          </section>
        </div>
      );
    }

    if (activeStep === 1) {
      return (
        <div className="onb-step-grid onb-identity-grid">
          <section className="onb-detail-panel onb-large-detail">
            <h3>Specialist details</h3>
            <div className="onb-detail-list">
              {profileRows.map((row) => (
                <div key={row.label}>
                  <span><row.icon size={18} /> {row.label}</span>
                  {row.link ? <a href="#profile-preview">{row.value}</a> : <strong>{row.value}</strong>}
                </div>
              ))}
            </div>
            <p className="onb-why-note">
              <ShieldCheck size={16} /> This helps GPs reach the right specialist without phoning the practice.
            </p>
          </section>

          <section className="onb-decision-panel">
            <h3>Is this information correct?</h3>
            <div className="onb-option-stack">
              <OptionButton
                icon={Check}
                label="Yes"
                helper="Continue with these details"
                selected={identityChoice === "yes"}
                tone="positive"
                onClick={() => setIdentityChoice("yes")}
              />
              <OptionButton
                icon={X}
                label="No"
                helper="Flag this for correction"
                selected={identityChoice === "no"}
                tone="danger"
                onClick={() => setIdentityChoice("no")}
              />
              <OptionButton
                icon={CircleHelp}
                label="Not sure"
                helper="PulseBoard can check later"
                selected={identityChoice === "not_sure"}
                tone="muted"
                onClick={() => setIdentityChoice("not_sure")}
              />
            </div>
          </section>
        </div>
      );
    }

    if (activeStep === 2) {
      return (
        <div className="onb-step-stack">
          <div className="onb-choice-grid">
            <OptionButton
              icon={Check}
              label="Yes"
              helper="GPs can send referrals"
              selected={availability === "yes"}
              tone="positive"
              onClick={() => setAvailability("yes")}
            />
            <OptionButton
              icon={Clock3}
              label="Urgent only"
              helper="Routine referrals paused"
              selected={availability === "urgent"}
              tone="urgent"
              onClick={() => setAvailability("urgent")}
            />
            <OptionButton
              icon={X}
              label="No"
              helper="Do not show as available"
              selected={availability === "no"}
              tone="danger"
              onClick={() => setAvailability("no")}
            />
            <OptionButton
              icon={CircleHelp}
              label="Not sure"
              helper="We will check again"
              selected={availability === "not_sure"}
              tone="muted"
              onClick={() => setAvailability("not_sure")}
            />
          </div>

          <section className="onb-field-block">
            <h3><Clock3 size={18} /> Typical wait time</h3>
            <div className="onb-pill-row">
              {WAIT_BUCKET_OPTIONS.map((option) => (
                <PillButton
                  key={option.value}
                  label={option.label}
                  selected={waitBucket === option.value}
                  onClick={() => setWaitBucket(option.value)}
                />
              ))}
            </div>
          </section>

          <section className="onb-field-block">
            <h3><Send size={18} /> How should practices send referrals?</h3>
            <div className="onb-pill-row">
              {REFERRAL_METHOD_OPTIONS.map((method) => (
                <PillButton
                  key={method.value}
                  label={method.label}
                  selected={referralMethod === method.value}
                  onClick={() => setReferralMethod(method.value)}
                />
              ))}
            </div>
            {referralMethod === "email" ? (
              <label className="onb-referral-field">
                Referral email
                <input
                  type="email"
                  value={referralEmail}
                  onChange={(event) => setReferralEmail(event.target.value)}
                  placeholder="referrals@practice.com.au"
                />
              </label>
            ) : null}
            {referralMethod === "fax" ? (
              <label className="onb-referral-field">
                Referral fax
                <input
                  value={referralFax}
                  onChange={(event) => setReferralFax(event.target.value)}
                  inputMode="tel"
                  placeholder="02 0000 0000"
                />
              </label>
            ) : null}
            <label className="onb-referral-field">
              What to include <small>Optional</small>
              <textarea
                value={referralNotes}
                onChange={(event) => setReferralNotes(event.target.value)}
                maxLength={600}
                rows={3}
                placeholder="For example: referral letter, recent imaging or pathology"
              />
              <span>{referralNotes.length}/600</span>
            </label>
            <p className="onb-why-note">
              <ShieldCheck size={16} /> This helps GPs send referrals the right way, first time.
            </p>
          </section>
        </div>
      );
    }

    if (activeStep === 3) {
      return (
        <div className="onb-step-stack">
          <div className="onb-owner-grid">
            {ownerOptions.map((option) => (
              <OptionButton
                key={option.label}
                icon={option.icon}
                label={option.label}
                helper={option.helper}
                selected={owner === option.label}
                tone={option.label === "Reception / practice manager" ? "positive" : "default"}
                onClick={() => setOwner(option.label)}
              />
            ))}
          </div>

          <section className="onb-reminder-card">
            <span><Bell size={23} /></span>
            <div>
              <h3>Monthly reminder</h3>
              <p>Ask once a month whether this profile is still correct.</p>
            </div>
            <button
              type="button"
              className={`onb-toggle${reminderEnabled ? " is-on" : ""}`}
              aria-pressed={reminderEnabled}
              aria-label="Monthly reminder"
              onClick={() => setReminderEnabled((enabled) => !enabled)}
            >
              <span />
            </button>
          </section>
        </div>
      );
    }

    if (activeStep === 4) {
      return (
        <div className="onb-step-stack" id="profile-preview">
          <article className="onb-gp-card">
            <div className="onb-gp-header">
              <div className="onb-avatar is-small">{displayProfile.initials}</div>
              <div>
                <h3>{displayProfile.name} <ShieldCheck size={19} /></h3>
                <p>{displayProfile.specialty} <span /> {displayProfile.practice}</p>
                <p><MapPin size={16} /> {displayProfile.location} <span /> {displayProfile.phone}</p>
              </div>
            </div>

            <div className="onb-gp-pills">
              <span className={availability === "no" ? "is-danger" : "is-green"}>
                {availability === "no" ? <X size={17} /> : <Check size={17} />}
                {referralSummary}
              </span>
              <span><Clock3 size={17} /> {waitBucketLabel(waitBucket) ?? "Wait not published"}</span>
              <span><Send size={17} /> {referralMethodLabel(referralMethod) ?? "Method not published"}</span>
            </div>

            <div className="onb-about">
              <strong>About</strong>
              <p>
                {displayProfile.about}
              </p>
              <a href="#profile-preview">{displayProfile.website}</a>
            </div>
          </article>

          <div className="onb-preview-actions">
            <button
              type="button"
              className={`onb-action-button is-primary${approvalState === "approved" ? " is-selected" : ""}`}
              onClick={() => void handleApprove()}
              disabled={saveAvailability.isPending}
            >
              <CheckCircle2 size={22} /> {saveAvailability.isPending ? "Saving..." : "Approve profile"}
            </button>
            <button
              type="button"
              className={`onb-action-button${approvalState === "changes" ? " is-selected" : ""}`}
              onClick={() => setApprovalState("changes")}
            >
              <Send size={21} /> Request changes
            </button>
            <button
              type="button"
              className={`onb-action-button${approvalState === "later" ? " is-selected" : ""}`}
              onClick={() => setApprovalState("later")}
            >
              <Bookmark size={21} /> Save for later
            </button>
          </div>
          {localSaveNotice && (
            <p className="onb-save-notice">
              <ShieldCheck size={16} /> {localSaveNotice}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="onb-complete-grid">
        <section className="onb-complete-panel">
          <span className="onb-complete-icon"><CheckCircle2 size={34} /></span>
          <h2>Approved in PulseBoard.</h2>
          <p>
            Your profile is set to show as {referralSummary.toLowerCase()} with {waitBucket
              ? `a ${waitBucketLabel(waitBucket)} wait time`
              : "no published wait time"}. Reminders will go to {owner.toLowerCase()}.
          </p>
          <div className="onb-complete-summary">
            <span><ShieldCheck size={17} /> Specialist approved</span>
            <span><Clock3 size={17} /> Confirmed today</span>
            <span><Bell size={17} /> {reminderEnabled ? "Monthly reminder enabled" : "Reminder off"}</span>
          </div>
        </section>

        <section className="onb-phone-card" aria-label="Mobile approval preview">
          <div className="onb-phone">
            <div className="onb-phone-screen">
              <div className="onb-phone-status">
                <span>9:41</span>
                <span>...</span>
              </div>
              <div className="onb-phone-brand">
                <img src="/pulseboard-favicon-256.png" alt="" />
                <strong>PulseBoard</strong>
              </div>
              <div className="onb-phone-logo">
                <img src="/pulseboard-favicon-256.png" alt="" />
              </div>
              <div className="onb-phone-content">
                <h3>{displayProfile.practice} has prepared your PulseBoard profile.</h3>
                <p>Please review and approve.</p>
                <button type="button"><Check size={19} /> Approve</button>
                <button type="button" className="is-light"><Send size={18} /> Request changes</button>
                <button type="button" className="is-light"><UserRound size={18} /> Not me</button>
                <small><LockKeyhole size={13} /> Secure link - expires in 7 days</small>
              </div>
            </div>
          </div>
          <div className="onb-mobile-benefits">
            <span><Zap size={20} /> Fast approval</span>
            <span><ShieldCheck size={20} /> Secure and private</span>
            <span><Smartphone size={20} /> Mobile friendly</span>
          </div>
        </section>
      </div>
    );
  };

  return (
    <main className="onboarding-board">
      <SEO
        title="Specialist onboarding - PulseBoard"
        description="Confirm your specialist referral profile in a few clicks."
        path="/onboarding"
        noindex
      />
      <section className="onb-wizard-shell" aria-label="PulseBoard specialist onboarding">
        <header className="onb-wizard-header">
          <div className="onb-brand-lockup">
            <img className="onb-logo" src="/pulseboard-favicon-256.png" alt="PulseBoard" />
            <div>
              <span className="onb-brand-kicker">Secure specialist onboarding</span>
              <h1><span>PulseBoard</span> <span>Onboarding</span></h1>
              <p>Simple. Accurate. Always up to date.</p>
            </div>
          </div>

          <div className="onb-trust-strip" aria-label="PulseBoard trust signals">
            {trustItems.map((item) => (
              <article key={item.title} className="onb-trust-item">
                <item.icon size={31} strokeWidth={1.8} />
                <div>
                  <h2>{item.title}</h2>
                  <p>{item.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </header>

        <section className="onb-wizard-layout">
          <aside className="onb-sidebar-card">
            <div className="onb-sidebar-profile">
              <span className="onb-secure-chip">
                <LockKeyhole size={15} /> Private link
              </span>
              <div className="onb-avatar">{displayProfile.initials}</div>
              <h2>{displayProfile.name}</h2>
              <p>{displayProfile.specialty} at {displayProfile.practice}</p>
            </div>

            <div className="onb-progress-meter">
              <div>
                <span>Progress</span>
                <strong>{progressPercent}%</strong>
              </div>
              <i><span style={{ width: `${progressPercent}%` }} /></i>
            </div>

            <nav className="onb-step-list" aria-label="Onboarding steps">
              {wizardSteps.map((step, index) => (
                <button
                  key={step.shortTitle}
                  type="button"
                  className={`${index === activeStep ? "is-active" : ""}${index < activeStep ? " is-complete" : ""}`}
                  onClick={() => setActiveStep(index)}
                >
                  <span>
                    {index < activeStep ? <Check size={17} /> : <step.icon size={17} />}
                  </span>
                  <strong>{step.shortTitle}</strong>
                </button>
              ))}
            </nav>

            <div className="onb-sidebar-note">
              <ShieldCheck size={18} />
              <p>Only profile details are collected. No patient information should be entered here.</p>
            </div>
          </aside>

          <section className="onb-stage">
            <div className="onb-stage-heading">
              <div>
                <span>{stageHeading.eyebrow}</span>
                <h2>{stageHeading.title}</h2>
                <p>{stageHeading.copy}</p>
              </div>
              <div className="onb-stage-badge">
                <stageHeading.icon size={28} strokeWidth={1.8} />
              </div>
            </div>

            <ProgressBars activeIndex={Math.max(0, Math.min(activeStep, 4))} />

            <div className="onb-stage-card">
              {renderStep()}
            </div>

            <footer className="onb-stage-footer">
              <button
                type="button"
                className="onb-nav-button"
                onClick={goBack}
                disabled={activeStep === 0}
              >
                <ChevronLeft size={19} /> Back
              </button>

              <div className="onb-footer-status">
                <ShieldCheck size={16} />
                {activeStep < wizardSteps.length - 1
                  ? "You can change any answer before approval."
                  : "Approved profile details are ready for GP referral use."}
              </div>

              <button
                type="button"
                className="onb-nav-button is-primary"
                onClick={handlePrimaryAction}
                disabled={activeStep === wizardSteps.length - 1 || saveAvailability.isPending}
              >
                {primaryLabel} <ChevronRight size={19} />
              </button>
            </footer>
          </section>
        </section>
      </section>
    </main>
  );
}
